import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { Row, RowItemType, Section, SectionType } from '../../../models';
import { InstallationTypeSectionIds, ChannelInstallationFieldIds } from '../../common.models';
import { setArraySectionItems, setFieldValue } from '../../../actions';
import { Entries } from 'app/shared/models/add-location-channel';
import { field } from '../../../core/field-schema';
import { required, maxLength, pattern, min, max } from '../../../core/validator-builders';
import { InstallationConfigState } from '../../common.models';

export const getValidators = function (isMetric: boolean) {
    const validators = [required({ message: 'COMMON.FIELD_REQUIRED_ERROR' }), maxLength({ value: 100 })];

    if (isMetric) {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern,
                message: 'COMMON.NUMERIC_DECIMAL_PLACE_ERROR_METRIC',
            }),
        ];
    } else {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.numeric0_100000With2DecimalPlacesPattern,
                message: 'COMMON.NUMERIC_DECIMAL_PLACE_ERROR',
            }),
        ];
    }
};

export const channelRowGenerator =
    (state: InstallationConfigState) =>
    (index: number, toPopulate?: Entries): Row => {
        const settings = state.settings;
        if (!settings) {
            throw new Error('Location card settings are not loaded.');
        }
        const FieldIds = ChannelInstallationFieldIds;

        return {
            info: {
                id: `channel-entry-row-${index}`,
                panelClass: 'channel-entry-row custom-50p',
            },
            items: [
                field.number(`${FieldIds.Depth}-${index}`, 'depth', {
                    value: toPopulate ? toPopulate.depth : null,
                    placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.DEPTH',
                    placeholderSuffix: settings.depthUnit,
                    validators: settings.isMetric
                        ? [
                              required(),
                              maxLength({ value: 10 }),
                              pattern({
                                  value: REGEX_CONFIG.numeric0_10000With1DecimalPlacePattern,
                                  message: 'HOME.MAP.MARKER_LOCATION_DETAIL.ONE_PRECISION_ERROR',
                              }),
                          ]
                        : [
                              required(),
                              maxLength({ value: 10 }),
                              max({
                                  value: 393,
                                  message: 'HOME.MAP.MARKER_LOCATION_DETAIL.CHANNEL_DEPTH_PATTERN_ERROR',
                              }),
                              pattern({
                                  value: REGEX_CONFIG.numericDecimal2PlacePattern,
                                  message: 'HOME.MAP.MARKER_LOCATION_DETAIL.CHANNEL_DEPTH_PATTERN_ERROR',
                              }),
                          ],
                    panelClass: 'pad-t-6 required',
                    events: {
                        onChange: (value) => {
                            const formatted = Number(Number(value).toFixed(settings.isMetric ? 1 : 2));
                            return isNaN(formatted) || !value
                                ? []
                                : [setFieldValue({ fieldId: `${FieldIds.Depth}-${index}`, value: formatted })];
                        },
                    },
                }),
                field.number(`${FieldIds.Quantity}-${index}`, 'quantity', {
                    value: toPopulate ? toPopulate.quantity : null,
                    placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.QUANTITY',
                    validators: settings.isMetric
                        ? [
                              required(),
                              maxLength({ value: 10 }),
                              pattern({
                                  value: REGEX_CONFIG.numeric0_10000With1DecimalPlacePattern,
                                  message: 'HOME.MAP.MARKER_LOCATION_DETAIL.ONE_PRECISION_ERROR',
                              }),
                              max({ value: 10000, message: 'HOME.MAP.MARKER_LOCATION_DETAIL.ONE_PRECISION_ERROR' }),
                          ]
                        : [
                              required(),
                              maxLength({ value: 10 }),
                              max({ value: 1000, message: 'HOME.MAP.MARKER_LOCATION_DETAIL.THREE_PRECISION_ERROR' }),
                              pattern({
                                  value: REGEX_CONFIG.numericDecimal3PlacePattern,
                                  message: 'HOME.MAP.MARKER_LOCATION_DETAIL.THREE_PRECISION_ERROR',
                              }),
                          ],
                    panelClass: 'pad-t-6 required',
                    events: {
                        onChange: (value) => {
                            const formatted = Number(Number(value).toFixed(settings.isMetric ? 1 : 3));
                            return isNaN(formatted) || !value
                                ? []
                                : [setFieldValue({ fieldId: `${FieldIds.Quantity}-${index}`, value: formatted })];
                        },
                    },
                }),
            ],
        };
    };
export const channelInstallationConfig = (state: InstallationConfigState): Section => {
    const settings = state.settings;
    if (!settings) {
        throw new Error('Location card settings are not loaded.');
    }
    const details = state.locationDetails[0];

    const FieldIds = ChannelInstallationFieldIds;
    return {
        info: {
            id: InstallationTypeSectionIds.Channel,
            title: '',
            displayTitle: false,
        },
        rows: [
            {
                info: {
                    id: 'channel-range-row',
                    panelClass: 'full-width',
                },
                items: [
                    field.number(FieldIds.Range, 'range', {
                        value: details ? details.range : null,
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.HEIGHT',
                        placeholderSuffix: settings.depthUnit,
                        validators: getValidators(settings.isMetric),
                        panelClass: 'pad-t-6 full-width required',
                    }),
                ],
            },
            {
                info: {
                    id: 'channel-num-entries-row',
                    panelClass: 'full-width',
                },
                items: [
                    field.number(FieldIds.NumberOfEntries, 'numberOfEntries', {
                        value: details && details.entries ? details.entries.length : null,
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.CHANNEL_ENTRIES_LABEL',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.channelEntriesLimit,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.CHANNEL_ENTRY_ERROR',
                            }),
                            min({ value: 2 }),
                            max({ value: 257 }),
                        ],
                        panelClass: 'pad-t-6 full-width required',
                        events: {
                            onChange: (value) =>
                                !Number.isInteger(value) || value < 2 || value > 257
                                    ? []
                                    : [
                                          setArraySectionItems({
                                              fieldId: FieldIds.ArraySection,
                                              newItemsCount: Number(value),
                                          }),
                                      ],
                        },
                    }),
                ],
            },
            {
                info: {
                    id: 'channel-entries-container-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: FieldIds.EntriesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: FieldIds.ArraySection,
                                title: '',
                                displayTitle: false,
                                displayActionBtns: false,
                                type: SectionType.Array,
                                expanded: true,
                                expandable: false,
                                itemGenerator: channelRowGenerator(state),
                                panelClass: 'channel-entries-panel',
                            },
                            rows:
                                details && details.entries
                                    ? details.entries.map((v, i) => channelRowGenerator(state)(i, v))
                                    : [],
                        },
                    },
                ],
            },
        ],
    };
};
